import { useMemo } from 'react';

import { TConversionBatchIds } from '@/console/features/projects/hooks/use-conversion-batch-ids/use-conversion-batch-ids.types';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { FeatureEnum, ProjectFragment } from '@/shared/types/types.generated';

/**
 * Builds the id pairs for the conversion-status batch providers, narrowed to the
 * rows that will actually read a job from it.
 *
 * Projects are left out of both arrays when they have no convertible version or
 * their vendor doesn't support conversion: those rows render "-" without
 * consulting the batch, so their ids would only widen the request — and while
 * the backend drains a project's whole conversion history per id, each one we
 * drop is real read work saved. Keeping the arrays index-aligned also avoids one
 * being empty while the other isn't, which would skip the query for the page.
 */
export const useConversionBatchIds = (
  projects?: ProjectFragment[],
): TConversionBatchIds => {
  const { isFeatureSupported } = useVendor();

  return useMemo(() => {
    const projectIds: string[] = [];
    const versionIds: string[] = [];

    // The Project fragment already excludes PROJECT_BACKUP and caps the
    // selection at one version, so items[0] is the latest convertible version.
    projects?.forEach((project) => {
      const versionId = project.versions?.items?.[0]?.versionId;
      if (!versionId) {
        return;
      }

      // Same predicate ConversionStatus gates its chip on.
      if (
        !isFeatureSupported({ feature: FeatureEnum.ProjectConvert, ...project })
      ) {
        return;
      }

      projectIds.push(project.projectId);
      versionIds.push(versionId);
    });

    return { projectIds, versionIds };
  }, [projects, isFeatureSupported]);
};
